import BigNumber from 'bignumber.js'
import { Network, Token, TokenAmount } from 'config/types'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useAppSelector } from 'state'
import { useListNetworks, useListTokens, useTokenPriorities, useTokenUsdPrices } from 'state/app/hooks'

import { getNetworkIncludeFiatInfo, getNetworkInfo, isFiatNetwork, isHPNNetwork } from 'utils/network'
import { getNetworkType, validateUserAddressByNetwokType } from 'utils/token'
import { PaymentPageEnums } from 'views/Payment/Menu'

/// Utils
export const sortTokenByBalance = (
  [currency1, currency2]: [TokenAmount, TokenAmount],
  prices: { [key: string]: string },
): number => {
  return (
    currency1.amount &&
    currency2.amount &&
    currency2.amount
      .times(prices[currency2.token.code])
      .minus(currency1.amount.times(prices[currency1.token.code]))
      .toNumber()
  )
}

export const formatPaymentTokenKey = (tokenCode: string, tokenAddress: string, network: number) =>
  tokenCode && `${tokenCode}_&_${tokenAddress}_&_${network}`

export const extractPaymentTokenKey = (str: string) => {
  const [tokenCode, tokenAddress, network] = str.split('_&_')
  return {
    tokenCode: tokenCode || '',
    tokenAddress: tokenAddress || '',
    network: parseInt(network || '', 10),
  }
}

/// Hooks
export type TokenSelectItemProps = {
  token: Token
  amountRequest?: () => Promise<BigNumber> | null
}

export const useControlSelectToken = ({
  initialToken = null,
  onSelectToken,
}:
  | {
      initialToken: Token
      onSelectToken?: (token: Token) => void
    }
  | undefined) => {
  const [selectedToken, setSelectedToken] = useState<Token>(initialToken)
  const [selectedNetwork, setSelectedNetwork] = useState<Network>(null)

  const handleSelectToken = (token: Token) => {
    setSelectedToken(token)
    if (onSelectToken) {
      onSelectToken(token)
    }
  }

  const onSelectNetworkByToken = (token: Token) => {
    const network = getNetworkInfo(token?.network)?.network
    if (network) {
      setSelectedNetwork(network)
      handleSelectToken(token)
    }
  }

  return { selectedToken, onSelectToken: handleSelectToken, selectedNetwork, onSelectNetworkByToken }
}

export const useAvailableDepositTokens = ({
  selectedCurrency,
  onCurrencySelect,
  isIncludeFiat = false,
  hideHUSDToken = false,
  from,
}: {
  selectedCurrency: Token
  onCurrencySelect: (token: Token) => void
  isIncludeFiat?: boolean
  hideHUSDToken?: boolean
  from?: PaymentPageEnums
}) => {
  const wallet = useAppSelector((state) => state.auth.wallet)
  const listToken = useListTokens()
  const networkList = useListNetworks()

  const { handleSortTokens } = useTokenPriorities({
    from,
  })

  const { currenciesGroupSameCode, networksCurrencySameCode, currencies } = useMemo(() => {
    const groupCurrenciesMap: Record<string, boolean> = {}
    const groupCurrencies: Token[] = []

    const networksCurrencySameCode: Record<string, Network[]> = {}
    const _currencies = handleSortTokens(listToken)

    _currencies.forEach((token) => {
      if ((!isIncludeFiat && isFiatNetwork(token.network)) || (hideHUSDToken && isHPNNetwork(token.network))) return

      const uniqueKey = `${token.code}_${token.logo}`
      if (!groupCurrenciesMap[uniqueKey]) {
        groupCurrenciesMap[uniqueKey] = true
        groupCurrencies.push(token)
      }

      // build network list for each token code
      if (!networksCurrencySameCode[token.code]) {
        networksCurrencySameCode[token.code] = []
      }

      const networkInfo = networkList.find((n) => n.chainId === token.network)
      if (networkInfo && !networksCurrencySameCode[token.code].some((n) => n.chainId === networkInfo.chainId)) {
        networksCurrencySameCode[token.code].push(networkInfo)
      }
    })

    return {
      currenciesGroupSameCode: groupCurrencies,
      networksCurrencySameCode,
      currencies: _currencies,
    }
  }, [listToken, isIncludeFiat, networkList, handleSortTokens])

  const handleSelectNetwork = (network: Network) => {
    // const { native } = isIncludeFiat
    //   ? getNetworkInfo(network.chainId)
    //   : getNetworkIncludeFiatInfo(network.chainId, { getFiatDefaultNative: true })

    // if (!selectToken || selectToken(native)) onCurrencySelect(native)
    // else {
    //   onCurrencySelect(listToken.find((t) => t.network === network.chainId && selectToken(t)))
    // }

    onCurrencySelect(listToken.find((t) => t.code === selectedCurrency.code && t.network === network.chainId))
  }

  const networkInfo = useMemo(
    () =>
      selectedCurrency &&
      (isIncludeFiat ? getNetworkIncludeFiatInfo(selectedCurrency.network) : getNetworkInfo(selectedCurrency.network)),
    [selectedCurrency, isIncludeFiat],
  )

  const isValidAddress =
    !wallet?.address || validateUserAddressByNetwokType(wallet?.address, getNetworkType(networkInfo?.network?.chainId))

  return useMemo(() => {
    return {
      currenciesGroupSameCode,
      networksCurrencySameCode,

      networkInfo,
      handleSelectNetwork,
      isValidAddress,
      currencies,
    }
  }, [networksCurrencySameCode, currencies, networkInfo, handleSelectNetwork, isValidAddress, currenciesGroupSameCode])
}

export const useFetchOptionsCurrencyBalance = ({
  curriencies,
  fetchTokenBalanceFn,
}: {
  curriencies: Token[]
  fetchTokenBalanceFn?: (token: Token) => Promise<BigNumber>
  from: PaymentPageEnums
}): {
  fetchBalance: (token: Token) => Promise<BigNumber>
  currencies: TokenAmount[]
  currenciesMap: {
    [key: string]: TokenAmount
  }
  isFetchedBalance: boolean
} => {
  const [balances, setBalances] = useState<{
    [tokenCode_ChainId: string]: TokenAmount
  }>({})

  const currecyFetchedBalanceRef = useRef({})
  const prices = useTokenUsdPrices()

  const fetchBalance = useCallback(
    async (token: Token) => {
      const key = formatPaymentTokenKey(token.code, token.address, token.network)
      if (!fetchTokenBalanceFn || currecyFetchedBalanceRef.current[key]) {
        return balances[key]?.amount
      }

      currecyFetchedBalanceRef.current[key] = true

      const balance = await fetchTokenBalanceFn(token)

      setBalances((preState) => ({
        ...preState,
        [key]: {
          token,
          amount: balance,
        },
      }))

      return balance
    },
    [curriencies, fetchTokenBalanceFn],
  )

  return useMemo(() => {
    const currenciesBalance = curriencies
      .map((token) => ({
        token,
        amount: balances[formatPaymentTokenKey(token.code, token.address, token.network)]?.amount,
      }))
      .sort((option1, option2) => sortTokenByBalance([option1, option2], prices))
      .filter((item) => item)

    return {
      fetchBalance,
      currencies: currenciesBalance,
      currenciesMap: currenciesBalance.reduce((state, currencyAmount) => {
        state[
          formatPaymentTokenKey(currencyAmount.token.code, currencyAmount.token.address, currencyAmount.token.network)
        ] = currencyAmount
        return state
      }, {}),
      isFetchedBalance: Object.keys(currecyFetchedBalanceRef.current).length > 0,
    }
  }, [curriencies, balances, prices, fetchBalance])
}
